export const designSystemColors = {
  black: '#000000',
  white: '#ffffff',
  whiteAlpha: {
    5: '#ffffff0d',
    10: '#ffffff1a',
    20: '#ffffff33',
    30: '#ffffff4d',
    40: '#ffffff66',
    50: '#ffffff80',
    60: '#ffffff99',
    70: '#ffffffb2',
    80: '#ffffffcc',
    90: '#ffffffe5',
  },
  blackAlpha: {
    5: '#0000000d',
    10: '#0000001a',
    20: '#00000033',
    30: '#0000004d',
    40: '#00000066',
    50: '#00000080',
    60: '#00000099',
    70: '#000000b2',
    80: '#000000cc',
    90: '#000000e5',
  },
  neutral: {
    50: '#fafaf9',
    100: '#f5f5f4',
    200: '#e3e1df',
    300: '#d6d4d1',
    400: '#a9a49d',
    500: '#79736b',
    600: '#58554d',
    700: '#45423b',
    800: '#292624',
    900: '#1c1a17',
    950: '#0c0b09',
  },
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#390909',
  },
  green: {
    50: '#f4f9f4',
    100: '#e7f1e7',
    200: '#cfe3cf',
    300: '#a9ccaa',
    400: '#7bad7d',
    500: '#588f5a',
    600: '#427044',
    700: '#385d3a',
    800: '#304b31',
    900: '#293e2b',
    950: '#132014',
  },
  yellow: {
    50: '#fdf9e9',
    100: '#faf0c7',
    200: '#f6df92',
    300: '#f1c753',
    400: '#ebad24',
    500: '#db9617',
    600: '#bd7311',
    700: '#975211',
    800: '#7d4116',
    900: '#6b3618',
    950: '#3e1b0a',
  },
  blue: {
    50: '#f1f6fd',
    100: '#dfe9fa',
    200: '#c6d9f7',
    300: '#9fc1f1',
    400: '#75a3e9',
    500: '#5080e1',
    600: '#3c64d4',
    700: '#3251c3',
    800: '#2f439e',
    900: '#2a3b7e',
    950: '#1e264d',
  },
  semantic: {
    light: {
      border: {
        green: 'var(--green-300)',
        highEmpathy: 'var(--neutral-300)',
        primary: 'var(--neutral-200)',
        primarySelected: 'var(--neutral-950)',
        red: 'var(--red-400)',
        secondary: 'var(--neutral-100)',
      },
      fill: {
        blue: 'var(--blue-100)',
        disabled: 'var(--neutral-50)',
        green: 'var(--green-100)',
        primary: 'var(--neutral-900)',
        primaryHover: 'var(--neutral-800)',
        primaryPressed: 'var(--neutral-700)',
        red: 'var(--red-100)',
        redHover: 'var(--red-200)',
        redPressed: 'var(--red-300)',
        secondary: 'var(--neutral-100)',
        secondaryHover: 'var(--neutral-200)',
        secondaryPressed: 'var(--neutral-300)',
        selectedTextDetection: 'var(--yellow-100)',
      },
      icon: {
        bw: 'var(--white)',
        disabled: 'var(--neutral-300)',
        green: 'var(--green-700)',
        icon: 'var(--white)',
        onColor: 'var(--white)',
        primary: 'var(--neutral-800)',
        red: 'var(--red-600)',
        secondary: 'var(--neutral-500)',
        yellow: 'var(--yellow-400)',
      },
      shadow: {
        default: 'var(--black)',
      },
      surface: {
        background: 'var(--white)',
        zero: 'var(--white)',
        one: 'var(--neutral-100)',
        oneTransparent: 'var(--black-alpha-5)',
        two: 'var(--neutral-200)',
        three: 'var(--neutral-300)',
      },
      text: {
        disabled: 'var(--neutral-300)',
        disabledOnFill: 'var(--neutral-300)',
        green: 'var(--green-700)',
        greenOnFill: 'var(--green-800)',
        inverse: 'var(--white)',
        link: 'var(--blue-700)',
        linkHover: 'var(--blue-800)',
        onColor: 'var(--white)',
        primary: 'var(--neutral-950)',
        red: 'var(--red-600)',
        redOnFill: 'var(--red-700)',
        secondary: 'var(--neutral-500)',
        tertiary: 'var(--neutral-400)',
      },
    },
    dark: {
      border: {
        green: 'var(--green-900)',
        highEmpathy: 'var(--neutral-700)',
        primary: 'var(--neutral-800)',
        primarySelected: 'var(--neutral-50)',
        red: 'var(--red-900)',
        secondary: 'var(--neutral-900)',
      },
      fill: {
        blue: 'var(--blue-950)',
        disabled: 'var(--neutral-900)',
        green: 'var(--green-950)',
        primary: 'var(--neutral-100)',
        primaryHover: 'var(--neutral-200)',
        primaryPressed: 'var(--neutral-300)',
        red: 'var(--red-950)',
        redHover: 'var(--red-900)',
        redPressed: 'var(--red-800)',
        secondary: 'var(--neutral-800)',
        secondaryHover: 'var(--neutral-700)',
        secondaryPressed: 'var(--neutral-600)',
        selectedTextDetection: 'var(--yellow-950)',
      },
      icon: {
        bw: 'var(--white)',
        disabled: 'var(--neutral-800)',
        green: 'var(--green-500)',
        icon: 'var(--white)',
        onColor: 'var(--white)',
        primary: 'var(--neutral-200)',
        red: 'var(--red-400)',
        secondary: 'var(--neutral-500)',
        yellow: 'var(--yellow-600)',
      },
      shadow: {
        default: 'var(--black)',
      },
      surface: {
        background: 'var(--neutral-950)',
        zero: 'var(--neutral-900)',
        one: 'var(--neutral-800)',
        oneTransparent: 'var(--white-alpha-10)',
        two: 'var(--neutral-700)',
        three: 'var(--neutral-600)',
      },
      text: {
        disabled: 'var(--neutral-800)',
        disabledOnFill: 'var(--neutral-700)',
        green: 'var(--green-500)',
        greenOnFill: 'var(--green-300)',
        inverse: 'var(--neutral-950)',
        link: 'var(--blue-400)',
        linkHover: 'var(--blue-500)',
        onColor: 'var(--white)',
        primary: 'var(--white)',
        red: 'var(--red-400)',
        redOnFill: 'var(--red-200)',
        secondary: 'var(--neutral-400)',
        tertiary: 'var(--neutral-600)',
      },
    },
  },
} as const;

export const designSystemRadii = {
  sm: '2px',
  md: '4px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  '3xl': '24px',
  '4xl': '32px',
  '5xl': '48px',
  xl2: '16px',
  xl3: '24px',
  xl4: '32px',
  xl5: '48px',
  full: '999px',
} as const;

export const designSystemSpacing = {
  px: '1px',
  xxs: '2px',
  xs2: '4px',
  xs3: '6px',
  xs: '8px',
  sm: '12px',
  base: '16px',
  md: '20px',
  lg: '24px',
  xl: '28px',
  xl2: '32px',
  xl3: '40px',
  xl4: '48px',
  xl5: '60px',
  xl6: '72px',
  sizeXxs: '2px',
  sizeXs: '4px',
  sizeSm: '8px',
  sizeBase: '12px',
  sizeLg: '16px',
  sizeXl: '20px',
  size2xl: '24px',
  size3xl: '32px',
  size4xl: '40px',
  size5xl: '48px',
} as const;

export const designSystemSizes = {
  xxs: '2px',
  xs: '4px',
  sm: '8px',
  base: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '40px',
  '5xl': '48px',
} as const;

export const designSystemShadows = {
  sm: '0px 1px 3px 0px rgba(0, 0, 0, 0.05)',
  default:
    '0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 6px 0px rgba(0, 0, 0, 0.04)',
  md: '0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.04)',
  lg: '0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.05)',
  xl: '0px 10px 10px -5px rgba(0, 0, 0, 0.04), 0px 20px 25px -5px rgba(0, 0, 0, 0.06)',
  '2xl': '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
} as const;

export enum DesignSystemTypography {
  HEADLINE_H1 = 'headline-h1',
  HEADLINE_H2 = 'headline-h2',
  HEADLINE_H3 = 'headline-h3',
  HEADLINE_H4 = 'headline-h4',
  TEXT_EDITOR_TITLE = 'text-editor-title',
  TEXT_EDITOR_H1 = 'text-editor-h1',
  TEXT_EDITOR_H2 = 'text-editor-h2',
  TEXT_EDITOR_H3 = 'text-editor-h3',
  TEXT_EDITOR_H4 = 'text-editor-h4',
  TEXT_EDITOR_H5 = 'text-editor-h5',
  TEXT_EDITOR_BODY = 'text-editor-body',
  TEXT_EDITOR_LIST = 'text-editor-list',
  LABEL_1_REGULAR = 'label-1-regular',
  LABEL_1_SEMIBOLD = 'label-1-semibold',
  LABEL_2_REGULAR = 'label-2-regular',
  LABEL_2_SEMIBOLD = 'label-2-semibold',
  BODY_1_REGULAR = 'body-1-regular',
  BODY_1_BOLD = 'body-1-bold',
  BODY_2_REGULAR = 'body-2-regular',
  BODY_2_BOLD = 'body-2-bold',
  CAPTION_REGULAR = 'caption-regular',
  CAPTION_BOLD = 'caption-bold',
  MICRO_REGULAR = 'micro-regular',
  MICRO_BOLD = 'micro-bold',
}

export const designSystemTypography = {
  [DesignSystemTypography.HEADLINE_H1]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 35,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.HEADLINE_H2]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 29,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.HEADLINE_H3]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 24,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.HEADLINE_H4]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 20,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_TITLE]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 40,
    mobileFontSize: 32,
    fontWeight: 400,
    lineHeight: '1.2',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_H1]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 36,
    mobileFontSize: 29,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_H2]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 32,
    mobileFontSize: 26,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_H3]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 28,
    mobileFontSize: 23,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_H4]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 25,
    mobileFontSize: 20,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_H5]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 22,
    mobileFontSize: 18,
    fontWeight: 400,
    lineHeight: '1.3',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_BODY]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 20,
    mobileFontSize: 16,
    fontWeight: 400,
    lineHeight: '1.5',
    letterSpacing: '0',
  },
  [DesignSystemTypography.TEXT_EDITOR_LIST]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 20,
    mobileFontSize: 16,
    fontWeight: 400,
    lineHeight: '1.5',
    letterSpacing: '0',
  },
  [DesignSystemTypography.LABEL_1_REGULAR]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 17,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.LABEL_1_SEMIBOLD]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 17,
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.LABEL_2_REGULAR]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.LABEL_2_SEMIBOLD]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 15,
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.BODY_1_REGULAR]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 17,
    fontWeight: 400,
    lineHeight: '25px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.BODY_1_BOLD]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 17,
    fontWeight: 700,
    lineHeight: '25px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.BODY_2_REGULAR]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.BODY_2_BOLD]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '22px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.CAPTION_REGULAR]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '15px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.CAPTION_BOLD]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 13,
    fontWeight: 700,
    lineHeight: '15px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.MICRO_REGULAR]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 11,
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0',
  },
  [DesignSystemTypography.MICRO_BOLD]: {
    fontFamily: 'var(--font-primary), sans-serif',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '13px',
    letterSpacing: '0',
  },
} as const;
